import React, { useEffect, useState, useCallback, useRef } from "react";
import { firestore } from "../Firebase/FirebaseConfig";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from "firebase/firestore";

import "../styles/ActivityFeedView.css";
const ActivityFeedView = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);        // request in-flight
  const [firstTimeLoading, setFirstTimeLoading] = useState(true); // initial load
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);

  // Put lastVisible in a ref so updating it does NOT trigger a re-render
  const lastVisibleRef = useRef(null);

  // Fetches posts from /posts, ordered by timeStamp desc, 20 at a time.
  const fetchPosts = useCallback(
    async (loadMore = false) => {
      try {
        setLoading(true);
        setError("");


        // 1) Build base query: /posts, orderBy timeStamp descending, limit 20
        let q = query(
          collection(firestore, "posts"),
          orderBy("timeStamp", "desc"),
          limit(20)
        );

        // 2) If this is a "load more" request and we have a cursor, page after it
        if (loadMore && lastVisibleRef.current) {
          q = query(
            collection(firestore, "posts"),
            orderBy("timeStamp", "desc"),
            startAfter(lastVisibleRef.current),
            limit(20)
          );
        }

        // 3) Execute query
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          // 4) Map docs to local objects
          const newPosts = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // 5) Convert numeric timeStamp to a JS Date object if needed
          //    (only if your docs have a numeric timeStamp)
          newPosts.forEach((post) => {
            if (post.timeStamp) {
              // Firestore timestamps often stored as seconds, so multiply by 1000
              post.date = new Date(post.timeStamp * 1000);
            } else if (post.postedDate) {
              // fallback parse if you have a postedDate string
              // but numeric timeStamp is recommended for ordering
              const cleaned = post.postedDate.replace(" at ", " ");
              post.date = new Date(cleaned);
            }
          });

          // Update our lastVisibleRef
          lastVisibleRef.current =
            snapshot.docs[snapshot.docs.length - 1];

          // 7) Merge newPosts into existing feed or replace entirely
          setPosts((prev) =>
            loadMore ? [...prev, ...newPosts] : [...newPosts]
          );
          setHasMore(snapshot.docs.length === 20);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
        setError("We couldn't load the activity feed. Please try again shortly.");
      } finally {
        setLoading(false);
        if (!loadMore) setFirstTimeLoading(false);
      }
    },
    []
  );

  // On mount: load the first page (no "loadMore" = false)
  useEffect(() => {
    fetchPosts(false);
  }, [fetchPosts]);

  // Reattach the scroll listener whenever loading or lastVisible changes
  useEffect(() => {
    const handleScroll = () => {
      // console.log("Scrolling...");
      console.log("Loading:", loading);
      if (loading || !hasMore) return;
      const scrollPos =
        window.innerHeight + document.documentElement.scrollTop;
      const bottom = document.documentElement.offsetHeight;
      console.log("Scroll position:", scrollPos, "Document bottom:", bottom);

      // If near the bottom, load next page
      if (scrollPos >= bottom - 2) {
        fetchPosts(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore, fetchPosts]);

  // If loading and no posts yet => show your "overlay" or spinner
  if (firstTimeLoading) {
    return (
      <div className="feed-loading" role="status">
        <span className="feed-loading__mark">CRAFT</span>
        <p>Pouring your feed…</p>
      </div>
    );
  }

  if (error) {
    return <div className="content-state content-state--error">{error}</div>;
  }

  if (posts.length === 0) {
    return (
      <div className="content-state">
        <h2>Your activity feed is ready for a first pour.</h2>
        <p>Follow people and groups in the CRAFT app to see their latest posts here.</p>
      </div>
    );
  }

  return (
    <div className="posts-container">
      {posts.map((post) => (
        <div key={post.id} className="post">
          <div className="post-header-wrapper">
            <div className="post-header">
              {post.owner && (
                <>
                  <img
                    src={post.owner.profilePictureUrl}
                    alt={
                      (post.owner.firstName || "") +
                      " " +
                      (post.owner.lastName || "")
                    }
                    className="avatar"
                  />
                  <span className="username">
                    {(post.owner.firstName || "") +
                      " " +
                      (post.owner.lastName || "") +
                      (post.postedDate
                        ? ", " + post.postedDate
                        : "")
                    }
                  </span>
                </>
              )}
            </div>
          </div>
          {post.postURLString && (
            <img
              src={post.postURLString}
              alt={post.caption}
              className="post-image"
            />
          )}
          <p>{post.caption}</p>
          {/* Render any other post details */}
        </div>
      ))}
      {/* Show a small "loading more" indicator if fetching next page */}
      {loading && posts.length > 0 && <div>Loading more...</div>}
    </div>
  );
};

export default ActivityFeedView;
